import React from "react";
import PropTypes from "prop-types";
import { compose } from "redux";

import StockAiAnalysisLoadingIcon from "../../../../../assets/icons/StockAiAnalysisLoadingIcon";
import stockAiNoPermissionIcon from "../../../../../assets/icons/stock-ai-no-permission.svg";
import useStockAiAnalysis from "../../../../../hooks/useStockAiAnalysis";
import withTranslations from "../../../../../hocs/withTranslations";
import {
  STOCK_AI_ANALYSIS_STATUS,
  STOCK_AI_ANALYSIS_VIEW
} from "../../../../../lib/stockAiAnalysis/constants";
import LandingTypography from "../../../landingPage/pages/Homepage/components/LandingTypography";
import LoadingErrorIcon from "../../../../../assets/icons/loading-error.svg";
import IconMessage from "../../IconMessage";
import ReadingTypography from "../../ReadingTypography";
import AiAnalysisDisclaimer from "../components/AiAnalysisDisclaimer";

import "../styles/default.scss";

const STOCK_AI_NO_PERMISSION_ICON_SIZE = 104;
const STOCK_AI_LOADING_ICON_SIZE = 104;

const StockAiAnalysis = ({ market, ticker, type, translate }) => {
  const {
    html,
    status,
    warning,
    canRetry,
    retryAnalysis,
    view
  } = useStockAiAnalysis({
    market,
    ticker,
    type
  });

  let content = null;

  if (view === STOCK_AI_ANALYSIS_VIEW.ERROR) {
    content = (
      <IconMessage
        imageSrc={LoadingErrorIcon}
        imageHeight={143}
        imageWidth={192}
        title={translate("stock_ai_analysis_error")}
        linkText={
          canRetry ? translate("stock_ai_analysis_try_again") : undefined
        }
        onLinkClick={canRetry ? retryAnalysis : undefined}
      />
    );
  } else if (view === STOCK_AI_ANALYSIS_VIEW.NOT_AVAILABLE) {
    content = (
      <IconMessage
        imageSrc={stockAiNoPermissionIcon}
        imageHeight={STOCK_AI_NO_PERMISSION_ICON_SIZE}
        imageWidth={STOCK_AI_NO_PERMISSION_ICON_SIZE}
        title={translate("stock_ai_analysis_not_available")}
        description={translate("stock_ai_analysis_not_available_description")}
      />
    );
  } else if (view === STOCK_AI_ANALYSIS_VIEW.LOADING) {
    content = (
      <IconMessage
        icon={
          <StockAiAnalysisLoadingIcon
            width={STOCK_AI_LOADING_ICON_SIZE}
            height={STOCK_AI_LOADING_ICON_SIZE}
          />
        }
        title={translate("stock_ai_analysis_loading_title")}
        description={translate("stock_ai_analysis_loading_description")}
      />
    );
  } else {
    content = (
      <div className="stock-ai-analysis">
        <AiAnalysisDisclaimer />
        {status === STOCK_AI_ANALYSIS_STATUS.OUTDATED ? (
          <p className="stock-ai-analysis__warning">
            {warning || translate("stock_ai_analysis_outdated_warning")}
          </p>
        ) : null}
        <ReadingTypography html={html} />
      </div>
    );
  }

  return <LandingTypography>{content}</LandingTypography>;
};

StockAiAnalysis.propTypes = {
  market: PropTypes.string.isRequired,
  ticker: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired
};

export default compose(withTranslations)(StockAiAnalysis);
